import { Close, CopyAllRounded, Dashboard } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Box, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, IconButton, Stack, Typography } from '@mui/material'
import React, { useState } from 'react'
import { IApprovalRequest } from '../../definitions/Approval'
import { IContract } from '../../definitions/Contract'
import Formatter from '../../helpers/Formatter'
import ApprovalManager from '../../managers/ApprovalManager'
interface ContractSubmitModalProps {
    open: boolean
    onClose: () => void
    contract: IContract
    onAddVersion: (contract: IContract, includePdfs: boolean) => Promise<void>
    approval: IApprovalRequest
    isSubmitting?: boolean
    cloneStatus?: string
    schemaName?: string
}

const ContractSubmitModal: React.FC<ContractSubmitModalProps> = ({ open, onClose, contract, approval, isSubmitting, onAddVersion, cloneStatus, schemaName }) => {
    const [includePdfs, setIncludePdfs] = useState(true)
    const handleClone = async () => {
        await onAddVersion(contract, includePdfs)
        onClose()
    }

    const renderModalContent = () => {
        const lastChange = approval?.changes?.length ? approval?.changes[approval?.changes.length - 1] : null
        return (
            <>
                <DialogTitle sx={{ pb: 1 }}>
                    <Box display='flex' justifyContent='space-between' alignItems='center'>
                        <Typography variant='h6'>Contract Info</Typography>
                        <IconButton onClick={onClose} size='small'>
                            <Close />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ pb: 2 }}>
                    <Grid container spacing={2} mt={3}>
                        <Grid item xs={6}>
                            <Stack spacing={2}>
                                {ApprovalManager.statusField(approval?.status)}
                                {ApprovalManager.infoField('Contract Name', approval?.entityName)}
                                <Box alignItems='flex-start'>
                                    <Typography variant='subtitle2' color='text.secondary'>
                                        Schema used:
                                    </Typography>
                                    <Box sx={{ mt: 0.5 }}>
                                        <Chip
                                            icon={<Dashboard />}
                                            label={schemaName || 'Loading...'}
                                            color='default'
                                            variant='filled'
                                            size='medium'
                                            sx={{
                                                borderRadius: '999px',
                                                fontWeight: 600
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </Stack>
                        </Grid>
                        <Grid item xs={6}>
                            <Stack spacing={2}>
                                {ApprovalManager.infoField('Approved By', lastChange?.userName ?? 'N/A')}
                                {ApprovalManager.infoField('Approved At', Formatter.date(new Date(lastChange?.changedAt), 'mm/dd/yyyy hh:mm:ss'))}
                            </Stack>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogContent sx={{ pb: 2 }}>
                    <Box mb={3}>
                        <Typography variant='body1' gutterBottom>
                            The current approved version will remain live until the new version is approved and published.
                        </Typography>
                    </Box>
                    <Box sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                        <FormControlLabel
                            control={(
                                <Checkbox
                                    checked={includePdfs}
                                    onChange={e => {
                                        setIncludePdfs(e.target.checked)
                                    }}
                                    color='primary'
                                />
                            )}
                            label={(
                                <Typography variant='body2'>
                                    Include PDF attachments from previous version
                                </Typography>
                            )}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                    <LoadingButton
                        startIcon={<CopyAllRounded />}
                        loading={cloneStatus === 'loading'}
                        loadingPosition='start'
                        variant='contained'
                        onClick={handleClone}
                        sx={{ textTransform: 'none' }}
                        disabled={isSubmitting}>
                        Create New Version
                    </LoadingButton>
                </DialogActions>
            </>
        )
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth PaperProps={{ sx: { borderRadius: 2, minHeight: 300 } }}>
            {renderModalContent()}
        </Dialog>
    )
}

export default ContractSubmitModal
